import * as React from 'react';

interface Effect<T> {
  (isMounted: () => boolean): Promise<T> | T;
}

interface Destructor<T> {
  (result: T): void;
}

export function useAsyncEffect<T>(effect: Effect<T>, deps?: React.DependencyList): void;
export function useAsyncEffect<T>(
  effect: Effect<T>,
  destructor: Destructor<T>,
  deps?: React.DependencyList,
): void;

export function useAsyncEffect<T>(
  effect: Effect<T>,
  destructorOrDeps?: Destructor<T> | React.DependencyList,
  deps?: React.DependencyList,
) {
  const hasDestructor = typeof destructorOrDeps === 'function';

  const destructor = hasDestructor ? (destructorOrDeps as Destructor<T>) : undefined;

  // eslint-disable-next-line no-param-reassign
  deps = hasDestructor ? deps : (destructorOrDeps as React.DependencyList);

  React.useEffect(
    () => {
      let result: T;
      let mounted = true;

      const maybePromise = effect(() => mounted);

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      Promise.resolve(maybePromise).then((value) => {
        result = value;
      });

      return () => {
        mounted = false;
        destructor?.(result);
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    deps,
  );
}
